import {useContext, useEffect, useMemo} from 'react';

import {useTranslation} from 'react-i18next';
import {EVENTS, EventData, STATUS, Step, useJoyride} from 'react-joyride';

import {WalkthroughDefinition, WalkthroughStep} from './types';
import {WalkthroughContext} from './WalkthroughContext';
import {hasSeenWalkthrough, markWalkthroughSeen} from './walkthroughStorage';
import {WalkthroughTooltip} from './WalkthroughTooltip';
import {trackWalkthroughEvent} from './walkthroughTracking';
import {FeatureFlagContext} from '../../contexts/FeatureFlagContext/FeatureFlagContext';
import {greenColors} from '../../layouts/theme/designSystem';

interface Props {
  definition: WalkthroughDefinition;
  /**
   * When true, the tour is enabled. Set to false to defer mounting (e.g. while
   * the page is still loading the elements the tour points at).
   */
  enabled?: boolean;
  /**
   * Callback function that is called after a step is completed.
   */
  onStepAfter?: (args: {step: WalkthroughStep; data: EventData}) => void;
}

const DEFAULT_SCROLL_OFFSET = 24;

const getWalkthroughScrollOffset = (): number => {
  if (typeof window === 'undefined') {
    return DEFAULT_SCROLL_OFFSET;
  }

  const stickyOrFixedElements = Array.from(document.querySelectorAll<HTMLElement>('*')).filter((element) => {
    const styles = window.getComputedStyle(element);
    if (styles.position !== 'sticky' && styles.position !== 'fixed') {
      return false;
    }
    if (styles.display === 'none' || styles.visibility === 'hidden') {
      return false;
    }
    const rect = element.getBoundingClientRect();
    return rect.height > 0 && rect.bottom > 0 && rect.top <= 80;
  });

  if (!stickyOrFixedElements.length) {
    return DEFAULT_SCROLL_OFFSET;
  }

  const maxBottom = stickyOrFixedElements.reduce((max, element) => {
    const {bottom} = element.getBoundingClientRect();
    return Math.max(max, bottom);
  }, 0);

  // Keep a small breathing room below the sticky header stack.
  return Math.min(220, Math.max(DEFAULT_SCROLL_OFFSET, Math.round(maxBottom + 12)));
};

const buildJoyrideSteps = (steps: WalkthroughStep[], t: (key: string) => string): Step[] =>
  steps.map((step) => ({
    target: `[data-tour="${step.target}"]`,
    title: step.titleKey ? t(step.titleKey) : undefined,
    content: t(step.bodyKey),
    placement: step.placement ?? 'auto',
    skipBeacon: true,
    floatingOptions: step.disableArrow ? {hideArrow: true} : undefined,
  }));

export const Walkthrough = ({definition, enabled = true, onStepAfter}: Props) => {
  const {id, version, steps} = definition;
  const {t, i18n} = useTranslation();
  const {pendingRestartId, consumeRestart} = useContext(WalkthroughContext);
  const {flags} = useContext(FeatureFlagContext);
  const isFlagEnabled = flags.WALKTHROUGHS_ENABLED;
  const isActive = enabled && isFlagEnabled;

  const joyrideSteps = useMemo(
    () => buildJoyrideSteps(steps, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [steps, t, i18n.language]
  );

  const locale = useMemo(
    () => ({
      back: t('walkthrough.controls.back'),
      close: t('walkthrough.controls.close'),
      last: t('walkthrough.controls.done'),
      next: t('walkthrough.controls.next'),
      skip: t('walkthrough.controls.skip'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  const scrollOffset = useMemo(() => getWalkthroughScrollOffset(), []);

  const {controls, on, Tour} = useJoyride({
    steps: joyrideSteps,
    continuous: true,
    scrollToFirstStep: true,
    tooltipComponent: WalkthroughTooltip,
    locale,
    options: {
      primaryColor: greenColors[700],
      arrowColor: '#fff',
      backgroundColor: '#fff',
      textColor: greenColors[700],
      overlayColor: 'rgba(9, 44, 76, 0.55)',
      zIndex: 1300,
      buttons: ['back', 'close', 'primary', 'skip'],
      // In continuous mode the close (X) button advances to the next step by
      // default; 'skip' makes it end the tour as users expect.
      closeButtonAction: 'skip',
      showProgress: false,
      skipBeacon: true,
      scrollOffset,
    },
  });

  useEffect(() => {
    if (!isActive) return;
    if (hasSeenWalkthrough(id, version)) return;
    controls.start(0);
    trackWalkthroughEvent('started', {walkthroughId: id, version});
  }, [isActive, id, version, controls]);

  useEffect(() => {
    if (!isActive) return;
    if (pendingRestartId !== id) return;
    controls.start(0);
    consumeRestart(id);
    trackWalkthroughEvent('started', {walkthroughId: id, version});
  }, [pendingRestartId, id, version, isActive, consumeRestart, controls]);

  useEffect(() => {
    const offStepAfter = on(EVENTS.STEP_AFTER, (data) => {
      trackWalkthroughEvent('step_viewed', {
        walkthroughId: id,
        version,
        stepIndex: data.index,
        totalSteps: data.size,
      });
      const step = steps[data.index];
      if (step) {
        onStepAfter?.({step, data});
      }
    });

    const offTargetMissing = on(EVENTS.TARGET_NOT_FOUND, (data) => {
      trackWalkthroughEvent('target_missing', {
        walkthroughId: id,
        version,
        stepIndex: data.index,
        totalSteps: data.size,
      });
    });

    const offTourEnd = on(EVENTS.TOUR_END, (data) => {
      if (data.status === STATUS.FINISHED) {
        markWalkthroughSeen(id, version);
        trackWalkthroughEvent('completed', {walkthroughId: id, version});
      } else if (data.status === STATUS.SKIPPED) {
        markWalkthroughSeen(id, version);
        trackWalkthroughEvent('skipped', {walkthroughId: id, version});
      }
    });

    return () => {
      offStepAfter();
      offTargetMissing();
      offTourEnd();
    };
  }, [on, id, version, onStepAfter, steps]);

  if (!isActive) return null;

  return Tour;
};
